/* ============================================
   OUDAMIR — Premium Gallery Controller
   Handles: arrows, swipe, dots, fade transitions,
   keyboard nav. Works alongside Dawn's media-gallery.
   ============================================ */

(function () {
  'use strict';

  class OudamirGallery {
    constructor(galleryEl) {
      this.gallery = galleryEl;
      this.mediaGallery = galleryEl.querySelector('media-gallery');
      if (!this.mediaGallery) return;

      this.viewer = this.mediaGallery.querySelector('[id^="GalleryViewer"]');
      this.slides = Array.from(
        this.mediaGallery.querySelectorAll('.product__media-item')
      );
      this.thumbnails = this.mediaGallery.querySelector('[id^="GalleryThumbnails"]');

      if (this.slides.length <= 1) return;

      this.currentIndex = 0;
      this.isTransitioning = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.swipeThreshold = 40;
      this.isRTL = document.documentElement.dir === 'rtl';

      this._findActiveIndex();
      this._injectArrows();
      this._injectDots();
      this._bindEvents();
      this._updateDots();
    }

    /* --- Find currently active slide index --- */
    _findActiveIndex() {
      this.slides.forEach((slide, i) => {
        if (slide.classList.contains('is-active')) {
          this.currentIndex = i;
        }
      });
    }

    /* --- Inject Premium Arrow Buttons --- */
    _injectArrows() {
      const arrowsContainer = document.createElement('div');
      arrowsContainer.className = 'oudamir-gallery__arrows';
      arrowsContainer.innerHTML = `
        <button type="button" class="oudamir-gallery__arrow oudamir-gallery__arrow--prev" aria-label="Previous image">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button type="button" class="oudamir-gallery__arrow oudamir-gallery__arrow--next" aria-label="Next image">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <polyline points="9 6 15 12 9 18"></polyline>
          </svg>
        </button>
      `;

      // Insert arrows into the viewer (slider-component) which covers only the image area
      if (this.viewer) {
        this.viewer.style.position = 'relative';
        this.viewer.appendChild(arrowsContainer);
      }

      this.prevBtn = arrowsContainer.querySelector('.oudamir-gallery__arrow--prev');
      this.nextBtn = arrowsContainer.querySelector('.oudamir-gallery__arrow--next');
    }

    /* --- Inject Dot Indicators (Mobile) --- */
    _injectDots() {
      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'oudamir-gallery__dots';

      this.slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'oudamir-gallery__dot' + (i === this.currentIndex ? ' is-active' : '');
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.dataset.index = i;
        dotsContainer.appendChild(dot);
      });

      // Insert after the viewer inside the media-gallery
      if (this.viewer && this.viewer.nextSibling) {
        this.mediaGallery.insertBefore(dotsContainer, this.viewer.nextSibling);
      } else {
        this.mediaGallery.appendChild(dotsContainer);
      }

      this.dotsContainer = dotsContainer;
      this.dots = Array.from(dotsContainer.querySelectorAll('.oudamir-gallery__dot'));
    }

    /* --- Bind All Events --- */
    _bindEvents() {
      // Arrow clicks
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(this.currentIndex - 1);
      });

      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goTo(this.currentIndex + 1);
      });

      // Dot clicks
      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goTo(parseInt(dot.dataset.index, 10));
        });
      });

      // Touch swipe on the viewer area
      const touchTarget = this.viewer || this.mediaGallery;
      touchTarget.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
      touchTarget.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: false });
      touchTarget.addEventListener('touchend', this._onTouchEnd.bind(this), { passive: true });

      // Keyboard navigation (when gallery is focused or hovered)
      this.mediaGallery.setAttribute('tabindex', '0');
      this.mediaGallery.addEventListener('keydown', this._onKeyDown.bind(this));

      // Listen for Dawn's thumbnail clicks to sync our state
      if (this.thumbnails) {
        this.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
          thumb.querySelector('button')?.addEventListener('click', () => {
            // Find which slide matches this thumbnail's target
            const targetId = thumb.dataset.target;
            const idx = this.slides.findIndex(
              (s) => s.dataset.mediaId === targetId
            );
            if (idx >= 0) {
              this.currentIndex = idx;
              this._updateDots();
            }
          });
        });
      }

      // Listen for Dawn's slideChanged event (mobile swipe via Dawn's slider)
      if (this.viewer) {
        this.viewer.addEventListener('slideChanged', (e) => {
          if (e.detail && e.detail.currentElement) {
            const mediaId = e.detail.currentElement.dataset.mediaId;
            const idx = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
            if (idx >= 0) {
              this.currentIndex = idx;
              this._updateDots();
            }
          }
        });
      }

      // Listen for Dawn's media-gallery custom event (variant change, external triggers)
      this.mediaGallery.addEventListener('oudamir:gallery:change', (e) => {
        if (e.detail && e.detail.mediaId) {
          const idx = this.slides.findIndex(
            (s) => s.dataset.mediaId === e.detail.mediaId
          );
          if (idx >= 0 && idx !== this.currentIndex) {
            this.currentIndex = idx;
            this._updateDots();
          }
        }
      });
    }

    /* --- Touch handlers --- */
    _onTouchStart(e) {
      if (e.touches.length > 1) return;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchDeltaX = 0;
    }

    _onTouchMove(e) {
      if (e.touches.length > 1) return;
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
      const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);

      // If horizontal swipe is dominant, prevent scroll
      if (Math.abs(this.touchDeltaX) > deltaY && Math.abs(this.touchDeltaX) > 10) {
        e.preventDefault();
      }
    }

    _onTouchEnd() {
      if (Math.abs(this.touchDeltaX) > this.swipeThreshold) {
        if (this.touchDeltaX > 0) {
          this.goTo(this.currentIndex - 1); // Swipe right → previous
        } else {
          this.goTo(this.currentIndex + 1); // Swipe left → next
        }
      }
      this.touchDeltaX = 0;
    }

    /* --- Keyboard handler --- */
    _onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
    }

    /* --- Go to a specific slide --- */
    goTo(index) {
      if (this.isTransitioning) return;

      // Loop around
      if (index < 0) index = this.slides.length - 1;
      if (index >= this.slides.length) index = 0;
      if (index === this.currentIndex) return;

      this.isTransitioning = true;

      const targetSlide = this.slides[index];
      const mediaId = targetSlide.dataset.mediaId;

      // Use Dawn's media-gallery setActiveMedia if available
      if (this.mediaGallery.setActiveMedia) {
        this.mediaGallery.setActiveMedia(mediaId, false);
      } else {
        // Fallback: manually toggle is-active
        this.slides.forEach((s) => s.classList.remove('is-active'));
        targetSlide.classList.add('is-active');

        // On mobile, scroll to the slide
        if (window.innerWidth < 750 && this.viewer?.slider) {
          targetSlide.parentElement.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      this.currentIndex = index;
      this._updateDots();

      // Release transition lock after animation
      setTimeout(() => {
        this.isTransitioning = false;
      }, 500);
    }

    /* --- Update dot indicators --- */
    _updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });
    }
  }

  /* --- Initialize on DOM ready --- */
  function init() {
    const wrappers = document.querySelectorAll('.product__media-wrapper');
    wrappers.forEach((wrapper) => {
      if (!wrapper.dataset.oudamirGallery) {
        wrapper.dataset.oudamirGallery = 'initialized';
        new OudamirGallery(wrapper);
      }
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Slight delay to ensure Dawn's custom elements are registered
    requestAnimationFrame(() => {
      requestAnimationFrame(init);
    });
  }

  // Also re-init on section render (Shopify theme editor)
  document.addEventListener('shopify:section:load', init);
})();
